import type { FavoriteShortcut, Project, SmartList } from '@portfolio-pm/shared';
import { GripVertical } from 'lucide-react';
import { type FormEvent, type ReactNode, useRef, useState } from 'react';
import { SmartListForm, type SmartListFormValue } from './SmartListForm.tsx';
import { SidebarFloatingMenu } from './sidebar-menus.tsx';

/**
 * Row components for the sidebar lists: projects, saved views,
 * favourites, and tags, plus the shared per-row action menu and the
 * inline name editor. Extracted from Sidebar.tsx.
 */

export const DEFAULT_TAG_COLORS = [
  '#dc2626',
  '#ea580c',
  '#d97706',
  '#ca8a04',
  '#65a30d',
  '#16a34a',
  '#059669',
  '#0d9488',
  '#0891b2',
  '#0284c7',
  '#2563eb',
  '#4f46e5',
  '#7c3aed',
  '#9333ea',
  '#c026d3',
  '#db2777',
  '#e11d48',
  '#64748b',
  '#78716c',
  '#57534e',
  '#1f2937',
  '#0f766e',
  '#7f1d1d',
  '#9a3412',
] as const;

export type FavoriteItem =
  | { shortcut: FavoriteShortcut; project: Project }
  | { shortcut: FavoriteShortcut; smartList: SmartList };

export function shortcutKey(shortcut: FavoriteShortcut): string {
  return `${shortcut.kind}:${shortcut.id}`;
}

export function hashTag(tag: string): number {
  let hash = 0;
  for (const char of tag) hash = (hash * 31 + char.charCodeAt(0)) >>> 0;
  return hash % DEFAULT_TAG_COLORS.length;
}

export interface ProjectRowProps {
  project: Project;
  count: number;
  active: boolean;
  editing: boolean;
  manualSort: boolean;
  dragging: boolean;
  onSelect(): void;
  onStartEdit(): void;
  onCommitEdit(name: string): void;
  onCancelEdit(): void;
  onArchive(): void;
  onUnarchive(): void;
  isFavorite: boolean;
  onAddToFavorites(): void;
  onRemoveFromFavorites(): void;
  onDragStart(): void;
  onDragEnd(): void;
  onDrop(sourceProjectId: string): void;
}

export function ProjectRow({
  project,
  count,
  active,
  editing,
  manualSort,
  dragging,
  onSelect,
  onStartEdit,
  onCommitEdit,
  onCancelEdit,
  onArchive,
  onUnarchive,
  isFavorite,
  onAddToFavorites,
  onRemoveFromFavorites,
  onDragStart,
  onDragEnd,
  onDrop,
}: ProjectRowProps): JSX.Element {
  if (editing) {
    return (
      <li>
        <NameInput
          initialValue={project.name}
          placeholder="Project name"
          onCommit={onCommitEdit}
          onCancel={onCancelEdit}
        />
      </li>
    );
  }

  return (
    <li
      data-testid="project-row"
      data-project-id={project.id}
      data-active={active || undefined}
      draggable={manualSort}
      onDragStart={(event) => {
        if (!manualSort) return;
        event.dataTransfer.effectAllowed = 'move';
        event.dataTransfer.setData('text/plain', project.id);
        onDragStart();
      }}
      onDragEnd={onDragEnd}
      onDragOver={(event) => {
        if (!manualSort) return;
        event.preventDefault();
        event.dataTransfer.dropEffect = 'move';
      }}
      onDrop={(event) => {
        if (!manualSort) return;
        event.preventDefault();
        onDrop(event.dataTransfer.getData('text/plain'));
      }}
      className={`group relative rounded-md ${dragging ? 'opacity-55' : ''}`}
    >
      <div
        className={`grid min-h-[22px] items-center gap-1 rounded-sm py-px pl-2 text-[color:var(--app-text)] transition hover:bg-white/55 ${
          active ? 'bg-[color:var(--app-accent-soft)]' : ''
        }`}
        style={{
          gridTemplateColumns: manualSort
            ? 'auto minmax(0, 1fr) max-content'
            : 'minmax(0, 1fr) max-content',
        }}
      >
        {manualSort && (
          <GripVertical
            aria-hidden="true"
            size={13}
            strokeWidth={2}
            className="text-[color:var(--app-text-soft)]"
          />
        )}
        <button
          type="button"
          onClick={onSelect}
          onDoubleClick={onStartEdit}
          className={`min-w-0 text-left text-[13px] leading-[18px] ${
            project.archived ? 'text-[color:var(--app-text-soft)]' : 'text-[color:var(--app-text)]'
          } ${active ? 'font-semibold' : 'font-normal'}`}
        >
          <span className="truncate">
            {project.name}
            {project.archived ? ' (archived)' : ''}
          </span>
        </button>
        <SidebarActionMenu
          count={count}
          label="Project actions"
          actions={[
            ...(project.archived
              ? [
                  {
                    label: 'Unarchive',
                    testId: 'unarchive-project-button',
                    ariaLabel: `Unarchive ${project.name}`,
                    onSelect: onUnarchive,
                  },
                ]
              : []),
            {
              label: 'Rename',
              onSelect: onStartEdit,
            },
            isFavorite
              ? {
                  label: 'Remove from Favorites',
                  ariaLabel: `Remove ${project.name} from Favorites`,
                  onSelect: onRemoveFromFavorites,
                }
              : {
                  label: 'Add to Favorites',
                  ariaLabel: `Add ${project.name} to Favorites`,
                  onSelect: onAddToFavorites,
                },
            ...(project.inbox
              ? []
              : project.archived
                ? []
                : [
                    {
                      label: 'Archive',
                      testId: 'archive-project-button',
                      ariaLabel: `Archive ${project.name}`,
                      onSelect: onArchive,
                    },
                  ]),
          ]}
        />
      </div>
    </li>
  );
}

export interface SmartListRowProps {
  smartList: SmartList;
  count: number;
  active: boolean;
  editing: boolean;
  onSelect(): void;
  onStartEdit(): void;
  onCommitEdit(patch: SmartListFormValue): void;
  onCancelEdit(): void;
  onDelete(): void;
  isFavorite: boolean;
  onAddToFavorites(): void;
  onRemoveFromFavorites(): void;
}

export function SmartListRow({
  smartList,
  count,
  active,
  editing,
  onSelect,
  onStartEdit,
  onCommitEdit,
  onCancelEdit,
  onDelete,
  isFavorite,
  onAddToFavorites,
  onRemoveFromFavorites,
}: SmartListRowProps): JSX.Element {
  if (editing) {
    return (
      <li>
        <SmartListForm
          initialName={smartList.name}
          initialQuery={smartList.query}
          initialRuleTree={smartList.ruleTree}
          initialGroupBy={smartList.groupBy}
          initialGroupDirection={smartList.groupDirection}
          initialSort={smartList.sort}
          initialShowCompleted={smartList.showCompleted}
          initialShowTrashed={smartList.showTrashed}
          onCommit={onCommitEdit}
          onCancel={onCancelEdit}
        />
      </li>
    );
  }

  return (
    <li className="group relative rounded-md">
      <div
        className={`grid min-h-[22px] grid-cols-[minmax(0,1fr)_max-content] items-center gap-1 rounded-sm py-px pl-2 text-[color:var(--app-text)] transition hover:bg-white/55 ${
          active ? 'bg-[color:var(--app-accent-soft)]' : ''
        }`}
      >
        <button
          type="button"
          onClick={onSelect}
          onDoubleClick={onStartEdit}
          className={`min-w-0 text-left text-[13px] leading-[18px] text-[color:var(--app-text)] ${
            active ? 'font-semibold' : 'font-normal'
          }`}
        >
          <span className="truncate">{smartList.name}</span>
        </button>
        <SidebarActionMenu
          count={count}
          label="Saved view actions"
          actions={[
            {
              label: 'Edit',
              onSelect: onStartEdit,
            },
            isFavorite
              ? {
                  label: 'Remove from Favorites',
                  ariaLabel: `Remove ${smartList.name} from Favorites`,
                  onSelect: onRemoveFromFavorites,
                }
              : {
                  label: 'Add to Favorites',
                  ariaLabel: `Add ${smartList.name} to Favorites`,
                  onSelect: onAddToFavorites,
                },
            {
              label: 'Delete',
              ariaLabel: `Delete ${smartList.name}`,
              onSelect: onDelete,
            },
          ]}
        />
      </div>
    </li>
  );
}

export function FavoriteRow({
  item,
  count,
  active,
  dragging,
  onSelect,
  onRemove,
  onDragStart,
  onDragEnd,
  onDrop,
}: {
  item: FavoriteItem;
  count: number;
  active: boolean;
  dragging: boolean;
  onSelect(): void;
  onRemove(): void;
  onDragStart(): void;
  onDragEnd(): void;
  onDrop(sourceKey: string): void;
}): JSX.Element {
  const isProject = 'project' in item;
  const name = isProject ? item.project.name : item.smartList.name;
  const key = shortcutKey(item.shortcut);

  return (
    <li
      data-testid="favorite-row"
      data-favorite-key={key}
      draggable
      onDragStart={(event) => {
        event.dataTransfer.effectAllowed = 'move';
        event.dataTransfer.setData('text/plain', key);
        onDragStart();
      }}
      onDragEnd={onDragEnd}
      onDragOver={(event) => {
        event.preventDefault();
        event.dataTransfer.dropEffect = 'move';
      }}
      onDrop={(event) => {
        event.preventDefault();
        onDrop(event.dataTransfer.getData('text/plain'));
      }}
      className={`group relative rounded-md ${dragging ? 'opacity-55' : ''}`}
    >
      <div
        className={`grid min-h-[22px] grid-cols-[auto_minmax(0,1fr)_max-content] items-center gap-1 rounded-sm py-px pl-1 text-[color:var(--app-text)] transition hover:bg-white/55 ${
          active ? 'bg-[color:var(--app-accent-soft)]' : ''
        }`}
      >
        <GripVertical
          aria-hidden="true"
          size={13}
          strokeWidth={2}
          className="text-[color:var(--app-text-soft)]"
        />
        <button
          type="button"
          onClick={onSelect}
          className={`min-w-0 text-left text-[13px] leading-[18px] text-[color:var(--app-text)] ${
            active ? 'font-semibold' : 'font-normal'
          }`}
        >
          <span className="truncate">{name}</span>
        </button>
        <SidebarActionMenu
          count={count}
          label="Favorite actions"
          actions={[
            {
              label: 'Remove from Favorites',
              ariaLabel: `Remove ${name} from Favorites`,
              onSelect: onRemove,
            },
          ]}
        />
      </div>
    </li>
  );
}

export interface SidebarAction {
  label: string;
  ariaLabel?: string;
  testId?: string;
  onSelect(): void;
}

export function SidebarActionMenu({
  count,
  label,
  actions,
  menuContent,
}: {
  count: number;
  label: string;
  actions: SidebarAction[];
  menuContent?: ReactNode;
}): JSX.Element {
  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);

  return (
    <div className="group/action relative justify-self-end">
      <button
        ref={triggerRef}
        type="button"
        aria-label={label}
        aria-haspopup="menu"
        aria-expanded={open || undefined}
        onClick={(event) => {
          event.stopPropagation();
          setOpen((value) => !value);
        }}
        onBlur={() => window.setTimeout(() => setOpen(false), 120)}
        className="flex items-center justify-end"
      >
        <span
          className={`rounded-sm bg-[color:var(--app-panel-muted)] px-1 text-right text-[11px] font-semibold leading-4 tabular-nums text-[color:var(--app-text)] ${
            open ? 'hidden' : 'group-hover/action:hidden'
          }`}
        >
          {count}
        </span>
        <span
          className={`mr-0.5 h-3 w-3 bg-[color:var(--app-text-muted)] ${
            open ? 'inline-block' : 'hidden group-hover/action:inline-block'
          }`}
          style={{
            clipPath: 'path("M 0 0 v 4 h 9 l -3 3 l -3 -3 h -3 v 8 h 12 v -12 h -12 Z")',
          }}
          aria-hidden="true"
        >
          <span className="sr-only">Open</span>
        </span>
      </button>
      {open && (
        <SidebarFloatingMenu triggerRef={triggerRef} widthClassName="w-40">
          {actions.map((action) => (
            <button
              key={action.label}
              type="button"
              role="menuitem"
              aria-label={action.ariaLabel}
              data-testid={action.testId}
              onMouseDown={(event) => event.preventDefault()}
              onClick={(event) => {
                event.stopPropagation();
                setOpen(false);
                action.onSelect();
              }}
              className="block w-full px-2 py-1 text-left text-[color:var(--app-text)] hover:bg-[rgba(29,37,36,0.08)]"
            >
              {action.label}
            </button>
          ))}
          {menuContent}
        </SidebarFloatingMenu>
      )}
    </div>
  );
}

export function TagRow({
  tag,
  count,
  color,
  active,
  onSelect,
  onChangeColor,
  onRename,
}: {
  tag: string;
  count: number;
  color: string;
  active: boolean;
  onSelect(): void;
  onChangeColor(color: string): void;
  onRename(): void;
}): JSX.Element {
  return (
    <li className="group rounded-md">
      <div className="grid min-h-[22px] grid-cols-[minmax(0,1fr)_max-content] items-center gap-1 rounded-sm py-px pl-2 text-[color:var(--app-text)] transition hover:bg-[rgba(29,37,36,0.08)]">
        <button
          type="button"
          onClick={onSelect}
          className={`flex min-w-0 items-center gap-1.5 text-left text-[13px] leading-[18px] text-[color:var(--app-text)] ${
            active ? 'font-semibold' : 'font-normal'
          }`}
        >
          <span
            className="h-3 w-3 shrink-0 rounded-sm border border-white/80"
            style={{ backgroundColor: color }}
            aria-hidden="true"
          />
          <span className="truncate">#{tag}</span>
        </button>
        <SidebarActionMenu
          count={count}
          label="Tag actions"
          actions={[{ label: 'Rename', ariaLabel: `Rename ${tag}`, onSelect: onRename }]}
          menuContent={
            <div className="px-2 py-1.5">
              <p className="mb-1 text-[10px] font-semibold uppercase tracking-[0.14em] text-[color:var(--app-text-soft)]">
                Colour
              </p>
              <div className="grid grid-cols-6 gap-1">
                {DEFAULT_TAG_COLORS.map((preset) => (
                  <button
                    key={preset}
                    type="button"
                    role="menuitem"
                    aria-label={`Set ${tag} color ${preset}`}
                    onMouseDown={(event) => event.preventDefault()}
                    onClick={(event) => {
                      event.stopPropagation();
                      onChangeColor(preset);
                    }}
                    className="h-4 w-4 rounded-sm border border-white/80"
                    style={{ backgroundColor: preset }}
                  />
                ))}
              </div>
              <label className="mt-1.5 flex cursor-pointer items-center justify-between gap-2 text-[11px] text-[color:var(--app-text)]">
                Custom
                <input
                  type="color"
                  value={color}
                  onMouseDown={(event) => event.preventDefault()}
                  onChange={(event) => onChangeColor(event.target.value)}
                  className="h-5 w-5 cursor-pointer"
                />
              </label>
            </div>
          }
        />
      </div>
    </li>
  );
}

export function NameInput({
  initialValue = '',
  placeholder,
  onCommit,
  onCancel,
}: {
  initialValue?: string;
  placeholder?: string;
  onCommit(name: string): void;
  onCancel(): void;
}): JSX.Element {
  const [value, setValue] = useState(initialValue);

  const handleSubmit = (event: FormEvent<HTMLFormElement>): void => {
    event.preventDefault();
    onCommit(value);
  };

  return (
    <form onSubmit={handleSubmit}>
      <input
        // biome-ignore lint/a11y/noAutofocus: this input is conditionally mounted on user action
        autoFocus
        data-testid="project-name-input"
        value={value}
        placeholder={placeholder}
        onChange={(event) => setValue(event.target.value)}
        onBlur={() => onCommit(value)}
        onKeyDown={(event) => {
          if (event.key === 'Escape') {
            event.preventDefault();
            onCancel();
          }
        }}
        className="ui-input"
      />
    </form>
  );
}
